import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import cN from 'classnames';
import styles from './styles.module.scss';
import {
    filterTypesKey,
    I_FilterItem,
    I_Tag,
    I_Type,
    I_Category,
    I_Group,
    I_AirLine,
} from '@Redux/search/interfaces';
import { setFilterItems, setTempFilterItems, setTags } from '@Redux/search/actions';
import { RootState } from '@Redux/search/store';
import SlidePage from '@components/Common/SlidePage';
import SlideFooter from '@components/Common/SlideFooter';
import Skeleton from '@components/Common/Skeletons';
import FilterBlock from './subComponent/FilterBlock';

interface I_TitleKey {
    key: string;
    title: string;
    cols: number; // 幾個 item 一排
}
interface I_FiltersProps {
    isSlideOpen: boolean; // 是否啟動 M 版側開
    setSlideOpen: (isOpen: boolean) => void; // 讓 M 版篩選按紐啟動側開用 callback
    onConfirm: (finalItems: I_FilterItem[]) => void; // M 版按確認後
}

const travelTypeOptions = {
    [filterTypesKey.TravelType]: [
        {
            Text: '團體',
            Value: 1,
            note: '',
        },
        {
            Text: '團體自由行',
            Value: 2,
            note: '團體湊票享優惠',
        },
    ],
};

let filterTypesKeyArr: I_TitleKey[] = [
    {
        key: filterTypesKey.TravelType,
        title: '旅遊型態',
        cols: 1,
    },
    {
        key: filterTypesKey.DepartureList,
        title: '出發地',
        cols: 2,
    },
    {
        key: filterTypesKey.PriceRange,
        title: '預算',
        cols: 1,
    },
    {
        key: filterTypesKey.DayList,
        title: '旅遊天數',
        cols: 2,
    },
    {
        key: filterTypesKey.WeekDays,
        title: '出發星期',
        cols: 2,
    },
    {
        key: filterTypesKey.AirlineList,
        title: '航空公司',
        cols: 1,
    },
    {
        key: filterTypesKey.BuList,
        title: '聯營商品',
        cols: 1,
    },
];

let firstTimeHandleURL = true;
const urlParams = new URLSearchParams(window.location.search);
const DepartureID = urlParams.get('DepartureID');
const TravelType = urlParams.get('TravelType');
const BuIDs = urlParams.get('bu');
const PreferAirlines = urlParams.get('PreferAirlines');

const TravelTypeItem = (TravelType: string) => ({
    filterBy: filterTypesKey.TravelType,
    text: TravelType === '1' ? '團體' : '團體自由行',
    value: TravelType,
    subValue: '',
});

const url = new URL(location.href);
// 不區分大小寫抓取 Tags
const Tags: string | undefined =
    url.href.toLowerCase().indexOf('tags') !== -1
        ? url.href.toLowerCase().split('tags=')[1].split('&')[0] ||
          url.href.toLowerCase().split('tags=')[1]
        : undefined;

// 網址列 TravelType 為 2 時，為團體自由行，則篩選不顯示旅遊型態選項
if (TravelType === '2') {
    filterTypesKeyArr = filterTypesKeyArr.filter((type) => type.key !== filterTypesKey.TravelType);
}

// 網址列有出發地，則篩選不顯示出發地選項
if (DepartureID) {
    filterTypesKeyArr = filterTypesKeyArr.filter(
        (type) => type.key !== filterTypesKey.DepartureList
    );
}

const Filters: React.FC<I_FiltersProps> = ({ isSlideOpen, setSlideOpen, onConfirm }) => {
    const moduleName = 'filters';
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });

    const { DepthInfo, FilterItems, TempFilterItems, DepthInfoIsLoading } = useSelector(
        (state: RootState) => state.search
    );

    useEffect(() => {
        if (!DepthInfo || !firstTimeHandleURL) return; // 判斷為 API 回來後，且第一次才執行

        const transportArr: any[] = [];
        const newFilterItems: I_FilterItem[] = [];

        // 將 url 參數加入 filterItems 用來設定篩選的預設值
        const processFilterList = (
            list: any[],
            filterBy: string,
            option: { textKey: string; valueKey: string },
            urlParam: string | null
        ) => {
            // 比對現有的選項中找出是否有符合 url 的 option，防止 user 亂輸入參數
            const filteredList = list.filter(
                (item) => urlParam && urlParam.split(',').includes(item[option.valueKey])
            );

            filteredList.forEach((item) => {
                newFilterItems.push({
                    filterBy,
                    text: item[option.textKey],
                    value: item[option.valueKey],
                    subValue: '',
                });
            });
        };

        // 針對 Url 的 bu 部分做處理
        processFilterList(
            DepthInfo.BuList,
            filterTypesKey.BuList,
            {
                textKey: 'Name',
                valueKey: 'ID',
            },
            BuIDs
        );

        // 針對 Url 的 PreferAirlines 部分做處理
        processFilterList(
            DepthInfo.AirlineList,
            filterTypesKey.AirlineList,
            {
                textKey: 'AirlineName',
                valueKey: 'AirlineID',
            },
            PreferAirlines
        );

        // 針對 Url 的TravelType 部分做處理
        switch (TravelType) {
            case '1':
            case '2':
                newFilterItems.push(TravelTypeItem(TravelType));
                break;
            // TravelType 為 0 或 null 時，團體 & 團體自由行都包含
            default:
                newFilterItems.push(TravelTypeItem('1'));
                newFilterItems.push(TravelTypeItem('2'));
                break;
        }

        const setClientDefaultTags = (tag: string) => {
            DepthInfo.CategoryList.forEach(({ TypeList }: I_Category) => {
                TypeList.forEach(({ GroupList, TripTypeCode }: I_Type) => {
                    GroupList.forEach(({ TagList }: I_Group) => {
                        TagList.forEach(({ TagName, TagNo }: I_Tag) => {
                            const newVal = `${TagNo}`;
                            const props = {
                                filterBy: filterTypesKey.CategoryList,
                                subValue: '',
                                text: TagName,
                                value: newVal,
                            };
                            if (tag === newVal) {
                                newFilterItems.push(props);
                            }
                            // 控制讓當遊玩交通的子層亮起來也必須讓遊玩交通亮起來
                            if (newVal === tag && TripTypeCode !== null) {
                                if (transportArr.indexOf(TripTypeCode) === -1) {
                                    transportArr.push(TripTypeCode);
                                }
                            }
                        });
                    });
                });
            });
        };

        // 針對 Url 的 Tags 做處理
        if (Tags) {
            dispatch(setTags(Tags));
            FilterItems.forEach((item: I_FilterItem) => {
                newFilterItems.push(item);
            });
            // 當只有一個 Tag
            if (Tags.indexOf(',') === -1) {
                setClientDefaultTags(Tags);
            } else {
                // 當 Tags 有多個
                Tags.split(',').forEach((tag: string) => {
                    setClientDefaultTags(tag);
                });
            }
        }

        dispatch(setFilterItems(newFilterItems));
        firstTimeHandleURL = false;
    }, [DepthInfo, FilterItems, dispatch]);

    const renderFilterBlocks = () => {
        return (
            <ul className={styles.filterBlocks}>
                {filterTypesKeyArr.map((item) => {
                    const commonProps = {
                        cols: item.cols,
                        title: item.title,
                        filterType: item.key,
                        options: {
                            [item.key]: DepthInfo[item.key],
                        },
                    };

                    switch (item.key) {
                        case filterTypesKey.AirlineList:
                        case filterTypesKey.DepartureList:
                        case filterTypesKey.DayList:
                        case filterTypesKey.BuList:
                        case filterTypesKey.WeekDays:
                            if (!DepthInfo[item.key]) return; // 如果資料沒有此 key 不渲染區塊
                            if (DepthInfo[item.key].length === 0) return; // 篩選類別無子項則不渲染區塊
                            break;
                        case filterTypesKey.PriceRange:
                            if (!DepthInfo[item.key]) return;
                            commonProps.options = {
                                [filterTypesKey.PriceRange]: [
                                    {
                                        PriceStart: DepthInfo[item.key].PriceStart,
                                        PriceEnd: DepthInfo[item.key].PriceEnd,
                                    },
                                ],
                            };
                            break;
                        case filterTypesKey.TravelType:
                            commonProps.options = travelTypeOptions;
                            break;
                        default:
                            return;
                    }
                    return <FilterBlock key={item.key} data_cy={item.key} {...commonProps} />;
                })}
            </ul>
        );
    };

    if (!isMobile && DepthInfoIsLoading) {
        return <Skeleton part="filter" />;
    }

    return DepthInfo ? (
        <div className={cN(moduleName, styles[`${moduleName}`])}>
            {DepthInfo &&
                (isMobile ? (
                    <div className={styles[`${moduleName}_M`]}>
                        <SlidePage
                            title="篩選"
                            visible={isSlideOpen}
                            setIsVisible={() => {
                                setSlideOpen(false);
                                dispatch(setFilterItems(TempFilterItems));
                            }}
                        >
                            <>
                                <div className={styles.SlideContent}>{renderFilterBlocks()}</div>
                                <SlideFooter
                                    onClickLeftBtn={() => {
                                        const notTagFiltersItems = FilterItems.filter(
                                            (item: I_FilterItem) =>
                                                item.filterBy === filterTypesKey.CategoryList
                                        );
                                        dispatch(setFilterItems(notTagFiltersItems));
                                    }}
                                    onClickRightBtn={() => {
                                        setSlideOpen(false);
                                        dispatch(setTempFilterItems(FilterItems));
                                        onConfirm(FilterItems);

                                        dispatch(setTempFilterItems(FilterItems));
                                    }}
                                    leftBtnText="重設條件"
                                    rightBtnText="確定"
                                />
                            </>
                        </SlidePage>
                    </div>
                ) : (
                    renderFilterBlocks()
                ))}
        </div>
    ) : null;
};

export default Filters;
